import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import NodesContainer from './components/mindmap/NodesContainer';
import TopBar from './components/ui/TopBar';
import AuthDialog from './components/ui/AuthDialog';
import { REVIEW_MAP_FIXTURE } from './testing/reviewMapFixture';
import {
  getMyMindMap,
  getMindMapById,
  createMindMap,
  clearStoredAuth,
  exchangeGoogleOAuthCode,
  AUTH_CLEARED_EVENT,
  OAUTH_RETURN_URL_KEY,
} from './utils/mindmap/apiClient';
import {
  cacheOfflineMapDescriptor,
  readOfflineMapDescriptor,
} from './utils/mindmap/offlinePersistence';

// Detect whether we should treat the initial render as "loading a map".
// If there is already an auth token in localStorage or a one-time OAuth code
// in the URL, we know we'll be fetching a map from
// the backend, so we can show a loading indicator instead of a
// temporary empty map.
function detectInitialMapLoading() {
  if (typeof window === 'undefined') return false;

  try {
    const hasStoredToken = !!window.localStorage.getItem('mindmappi_auth_token');

    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
    const oauthCode = hashParams.get('oauthCode');

    const mapId = url.searchParams.get('map');
    return !!(hasStoredToken || oauthCode || mapId);
  } catch {
    return false;
  }
}

function parseJwt(token) {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = 4 - (base64.length % 4);
    if (padding !== 4) {
      base64 += '='.repeat(padding);
    }
    const json =
      typeof window !== 'undefined'
        ? window.atob(base64)
        : Buffer.from(base64, 'base64').toString('utf8');
    return JSON.parse(json);
  } catch {
    return null;
  }
}

const isLocalReviewMode = process.env.REACT_APP_REVIEW_MODE === 'true';

const App = () => {
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authDialogMode, setAuthDialogMode] = useState('signin');
  const [user, setUser] = useState(null);
  const [baseNodeTitle, setBaseNodeTitle] = useState(null);
  const [authMessage, setAuthMessage] = useState(null);
  const [mapSessionKey, setMapSessionKey] = useState(0);
  const [remoteMapData, setRemoteMapData] = useState(null);
  const [mapLoadError, setMapLoadError] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(() => detectInitialMapLoading());
  const [isMapHydrated, setIsMapHydrated] = useState(false);
  const pendingSessionBump = useRef(false);
  const didRunInitialSyncRef = useRef(false);
  const [debugMapInfo, setDebugMapInfo] = useState(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [mapFileActions, setMapFileActions] = useState(null);
  const saveMindMapRef = useRef(null);

  const handleRegisterMapFileActions = useCallback((actions) => {
    setMapFileActions(actions);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
      if (hashParams.get('beta') !== 'full') return;
      hashParams.delete('beta');
      const hash = hashParams.toString();
      window.history.replaceState(null, '', `${url.pathname}${url.search}${hash ? `#${hash}` : ''}`);
      setAuthDialogMode('waitlist');
      setAuthDialogOpen(true);
    } catch {
      // A malformed location must not prevent the sign-in screen from rendering.
    }
  }, []);

  // Only bump session once per sign-in flow
  const bumpMapSession = useCallback(() => {
    if (!pendingSessionBump.current) {
      pendingSessionBump.current = true;
      setTimeout(() => {
        setMapSessionKey((prev) => prev + 1);
        pendingSessionBump.current = false;
      }, 0);
    }
  }, []);

  const synchronizeMapsOnLogin = useCallback(
    (userId) => {
      if (!userId) return;
      bumpMapSession();
    },
    [bumpMapSession]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
  }, []);

  // Keep a small debug summary of the current map context in development.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const userId = user && user.id;

      // For the signed-in-only flow, debug info simply reflects the
      // currently focused user + any mindmap id present in the URL.
      let mindmapId = null;
      try {
        const url = new URL(window.location.href);
        const mapParam = url.searchParams.get('map');
        if (mapParam) {
          mindmapId = mapParam;
        }
      } catch {
        mindmapId = null;
      }

      let ownerUserId = null;
      try {
        const metaRaw = window.localStorage.getItem('mindMapAuthMeta');
        const meta = metaRaw ? JSON.parse(metaRaw) : null;
        ownerUserId = meta && meta.ownerUserId ? meta.ownerUserId : null;
      } catch {
        ownerUserId = null;
      }

      setDebugMapInfo({
        scope: userId ? 'user' : 'unauthenticated',
        ownerUserId,
        mindmapId,
        hasGuest: false,
      });
    } catch (err) {
      console.error('Failed to read debug map info:', err);
    }
  }, [user]);

  // Load the initial mind map (or create one) for the
  // current signed-in user. This effect re-runs whenever
  // we bump the map session key (for example, after login).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let cancelled = false;

    const run = async () => {
      const userId = user && user.id;
      let targetMindmapId = null;
      try {
        const url = new URL(window.location.href);
        const mapParam = url.searchParams.get('map');
        if (mapParam) {
          targetMindmapId = mapParam;
        }
      } catch {
        targetMindmapId = null;
      }

      // Anonymous visitors may open public maps and possession-based links,
      // but never load or create an account's primary map.
      if (!userId && !targetMindmapId) {
        setRemoteMapData(null);
        setMapLoadError(null);
        setIsMapLoading(false);
        return;
      }

      setIsMapLoading(true);
      setMapLoadError(null);
      setIsMapHydrated(false);

      let remote = null;

      try {
        if (targetMindmapId) {
          remote = await getMindMapById(targetMindmapId);
        } else if (userId) {
          remote = await getMyMindMap();
        }

        // If the user has no primary mind map yet, create one so we
        // always have a stable mindmapId for Hocuspocus/Yjs documents.
        if (userId && !remote && !targetMindmapId) {
          try {
            const created = await createMindMap(null);
            if (created && created.id) {
              console.log('[DEBUG] Created primary mind map for user:', created.id);
              remote = {
                mindmapId: created.id,
                ownerUserId: userId,
                accessRole: 'owner',
                accessSource: 'owner',
                nodes: [],
                relationships: [],
                root: created.rootNodeId || null,
                selectedId: created.rootNodeId || null,
                updatedAt: created.updatedAt || created.createdAt || null,
              };

              // Update the URL so this session is anchored to the
              // newly created mindmap id (e.g. ?map=<id>) for
              // deep-linking and consistent Yjs document names.
              try {
                const url = new URL(window.location.href);
                const params = url.searchParams;
                params.set('map', created.id);
                const newSearch = params.toString();
                const newUrl = `${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`;
                window.history.replaceState(null, '', newUrl);
              } catch {
                // ignore URL errors; map will still load in this session
              }
            }
          } catch (err) {
            console.error('Failed to create primary mind map for user:', err);
          }
        }

        // If we successfully loaded a map *without* an explicit
        // map query parameter, normalize the URL so the root map
        // can always be revisited and shared via ?map=<id>.
        if (remote && !targetMindmapId) {
          try {
            const url = new URL(window.location.href);
            const params = url.searchParams;
            const existing = params.get('map');
            if (remote.mindmapId && existing !== remote.mindmapId) {
              params.set('map', remote.mindmapId);
              const newSearch = params.toString();
              const newUrl = `${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`;
              window.history.replaceState(null, '', newUrl);
            }
          } catch {
            // ignore URL errors; map will still load in this session
          }
        }

        if (cancelled) return;

        // Store the remote map data so NodesContainer can load it directly.
        // Even if the nodes array is currently empty, we keep remote
        // so it provides a stable mindmapId for Yjs to use.
        if (remote) {
          const count = Array.isArray(remote.nodes) ? remote.nodes.length : 0;
          console.log('[DEBUG] Received map from database with', count, 'nodes');
          if (userId) cacheOfflineMapDescriptor(userId, remote);
          setRemoteMapData(remote);
          setMapLoadError(null);
        } else if (targetMindmapId) {
          setRemoteMapData(null);
          setMapLoadError('unavailable');
        } else {
          console.log('[DEBUG] No map in database, starting with fresh map');
          setRemoteMapData(null);
          setMapLoadError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load mind map from backend:', err);
          const cached = userId && targetMindmapId
            ? readOfflineMapDescriptor(userId, targetMindmapId)
            : null;
          if (cached) {
            setRemoteMapData(cached);
            setMapLoadError(null);
          } else {
            setRemoteMapData(null);
            setMapLoadError('failed');
          }
        }
      } finally {
        if (!cancelled) {
          // Backend fetch is complete; allow NodesContainer to mount.
          // We'll keep an overlay visible until the map reports that it
          // has fully hydrated from Yjs.
          setIsMapLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [user, mapSessionKey]);

  // Exchange the short-lived, single-use OAuth code for the application token.
  // Remove the code from the address bar before making the network request.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let code = null;
    try {
      const url = new URL(window.location.href);
      const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
      code = hashParams.get('oauthCode');
      if (code) {
        didRunInitialSyncRef.current = true;
        hashParams.delete('oauthCode');
        const newHash = hashParams.toString();
        const newUrl = `${url.pathname}${url.search}${newHash ? `#${newHash}` : ''}`;
        window.history.replaceState(null, '', newUrl);
      }
    } catch {
      code = null;
    }

    if (!code) return;
    let cancelled = false;

    const completeGoogleSignIn = async () => {
      try {
        const token = await exchangeGoogleOAuthCode(code);
        if (cancelled) return;
        const payload = parseJwt(token);
        if (!payload || !payload.userId) {
          throw new Error('Google sign-in returned an invalid application token');
        }
        try {
          window.localStorage.setItem('mindmappi_auth_token', token);
        } catch {
          // ignore storage errors
        }
        setUser({
          id: payload.userId,
          displayName: payload.displayName || null,
          pictureUrl: payload.pictureUrl || null,
        });
        try {
          window.localStorage.setItem(
            'mindMapAuthMeta',
            JSON.stringify({ ownerUserId: payload.userId || null })
          );
        } catch {
          // ignore storage errors
        }
        const name = payload.displayName || 'user';
        setAuthMessage(`Signed in as ${name}`);
        try {
          const returnUrl = window.sessionStorage.getItem(OAUTH_RETURN_URL_KEY);
          window.sessionStorage.removeItem(OAUTH_RETURN_URL_KEY);
          if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
            window.history.replaceState(null, '', returnUrl);
          }
        } catch {
          // Continue on the OAuth callback landing page.
        }
        synchronizeMapsOnLogin(payload.userId);
      } catch (error) {
        if (!cancelled) {
          console.error('Google sign-in failed:', error);
          setAuthMessage('Google sign-in failed. Please try again.');
          setIsMapLoading(false);
        }
      }
    };

    completeGoogleSignIn();
    return () => {
      cancelled = true;
    };
  }, [synchronizeMapsOnLogin]);

  // On initial load, restore user from existing token if present
  // Only trigger a session bump after all reconciliation is done.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // If we've already performed an initial synchronization as part of
    // processing an OAuth code from the URL, skip this silent token
    // restoration to avoid running synchronizeMapsOnLogin twice on the
    // same page load (which would cause multiple map reloads).
    if (didRunInitialSyncRef.current) return;
    let pendingUserId = null;
    try {
      const token = window.localStorage.getItem('mindmappi_auth_token');
      if (token) {
        const payload = parseJwt(token);
        if (payload) {
          setUser({
            id: payload.userId,
            displayName: payload.displayName || null,
            pictureUrl: payload.pictureUrl || null,
          });
          try {
            window.localStorage.setItem(
              'mindMapAuthMeta',
              JSON.stringify({ ownerUserId: payload.userId || null })
            );
          } catch {
            // ignore storage errors
          }
          pendingUserId = payload.userId;
        }
      }
    } catch {
      // ignore
    }
    if (pendingUserId) {
      synchronizeMapsOnLogin(pendingUserId);
    }
    // Do NOT bumpMapSession here
  }, [synchronizeMapsOnLogin]);

  // Auto-hide auth feedback message after a short delay
  useEffect(() => {
    if (!authMessage) return;
    const id = setTimeout(() => setAuthMessage(null), 3000);
    return () => clearTimeout(id);
  }, [authMessage]);

  useEffect(() => {
    if ((!user && !remoteMapData) || isMapLoading || isMapHydrated || mapLoadError) return;
    const timeout = setTimeout(() => {
      setMapLoadError('sync-timeout');
    }, 20000);
    return () => clearTimeout(timeout);
  }, [isMapHydrated, isMapLoading, mapLoadError, remoteMapData, user]);

  // Keep the browser tab title in sync with the focused base node
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (baseNodeTitle) {
      document.title = `mindmappi - ${baseNodeTitle}`;
    } else {
      document.title = 'mindmappi';
    }
  }, [baseNodeTitle]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleAuthCleared = (event) => {
      const reason = event && event.detail && event.detail.reason;
      if (reason === 'manual') {
        return;
      }

      setUser(null);
      if (reason === 'expired') {
        setAuthMessage('Session expired. Please sign in again.');
      }
      setAuthDialogMode('signin');
      setAuthDialogOpen(true);
      bumpMapSession();
    };

    window.addEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
    return () => window.removeEventListener(AUTH_CLEARED_EVENT, handleAuthCleared);
  }, [bumpMapSession]);

  const handleAuthSuccess = useCallback((token) => {
    if (!token) return;
    try {
      window.localStorage.setItem('mindmappi_auth_token', token);
    } catch {
      // ignore storage errors
    }
    const payload = parseJwt(token);
    if (payload) {
      setUser({
        id: payload.userId,
        displayName: payload.displayName || null,
        pictureUrl: payload.pictureUrl || null,
      });
      try {
        window.localStorage.setItem(
          'mindMapAuthMeta',
          JSON.stringify({ ownerUserId: payload.userId || null })
        );
      } catch {
        // ignore storage errors
      }
      const name = payload.displayName || 'user';
      setAuthMessage(`Signed in as ${name}`);

      // Reconcile maps for manual auth as well.
      synchronizeMapsOnLogin(payload.userId);
    }
  }, [synchronizeMapsOnLogin]);

  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);

    try {
      // Best-effort: flush any in-flight edits to the
      // backend before we drop the auth token, so the
      // signed-in map is fully up to date when the
      // user next logs in.
      try {
        if (saveMindMapRef.current) {
          await saveMindMapRef.current();
        }
      } catch (err) {
        console.error('Failed to save mind map on sign out:', err);
      }

      clearStoredAuth('manual');

      setUser(null);
      bumpMapSession();
    } finally {
      setIsSigningOut(false);
    }
  }, [bumpMapSession]);

  const handleSignInClick = useCallback(() => {
    setAuthDialogMode('signin');
    setAuthDialogOpen(true);
  }, []);

  const handleRetryMapLoad = useCallback(() => {
    setMapLoadError(null);
    setIsMapLoading(true);
    setIsMapHydrated(false);
    bumpMapSession();
  }, [bumpMapSession]);

  const handleOpenMyMap = useCallback(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('map');
      const search = url.searchParams.toString();
      window.history.replaceState(
        null,
        '',
        `${url.pathname}${search ? `?${search}` : ''}${url.hash}`
      );
    } catch {
      // A URL update failure should not prevent the session from retrying.
    }
    setRemoteMapData(null);
    setMapLoadError(null);
    setIsMapLoading(true);
    setIsMapHydrated(false);
    bumpMapSession();
  }, [bumpMapSession]);

  const authToastStyle = {
    position: 'fixed',
    bottom: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '8px 14px',
    borderRadius: 999,
    backgroundColor: 'rgba(17, 24, 39, 0.9)',
    color: '#f9fafb',
    fontSize: 13,
    fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.25)',
    zIndex: 250,
  };

  const debugBoxStyle = {
    position: 'fixed',
    bottom: 20,
    left: 20,
    padding: '6px 10px',
    borderRadius: 6,
    backgroundColor: 'rgba(15, 23, 42, 0.85)',
    color: '#e5e7eb',
    fontSize: 11,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    zIndex: 260,
    pointerEvents: 'none',
    maxWidth: 260,
    lineHeight: 1.4,
  };


  return (
    <>
      <TopBar
        onSignInClick={handleSignInClick}
        user={user}
        baseNodeTitle={baseNodeTitle}
        currentMap={isLocalReviewMode ? REVIEW_MAP_FIXTURE : remoteMapData}
        mapFileActions={mapFileActions}
        onSignOut={handleSignOut}
      />
      <AuthDialog
        isOpen={authDialogOpen}
        onClose={() => setAuthDialogOpen(false)}
        onAuthSuccess={handleAuthSuccess}
        initialMode={authDialogMode}
      />
      {!user && !remoteMapData && !isMapLoading && !isLocalReviewMode ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background:
              'radial-gradient(circle at top, rgba(30,64,175,0.35), transparent 55%), rgba(15,23,42,0.97)',
            color: '#e5e7eb',
            fontFamily:
              'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            zIndex: 150,
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: 420, padding: '0 24px' }}>
            <div style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>
              Sign in to start mapping
            </div>
            <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 20 }}>
              Real-time collaboration and persistence require a signed-in
              account.
            </div>
            <button
              type="button"
              onClick={handleSignInClick}
              style={{
                padding: '10px 18px',
                borderRadius: 999,
                border: 'none',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                background: '#22c55e',
                color: '#022c22',
                boxShadow:
                  '0 10px 15px -3px rgba(0,0,0,0.4), 0 4px 6px -4px rgba(0,0,0,0.4)',
              }}
            >
              Sign in / Sign up
            </button>
          </div>
        </div>
      ) : mapLoadError ? (
        <main
          className="mindmap-access-error"
          role="alert"
          aria-labelledby="mindmap-access-error-title"
        >
          <div className="mindmap-access-error__panel">
            <div className="mindmap-access-error__symbol" aria-hidden="true">↗</div>
            <h1 id="mindmap-access-error-title">
              {mapLoadError === 'unavailable'
                ? 'This map isn’t available'
                : mapLoadError === 'sync-timeout'
                  ? 'This map is taking too long to connect'
                : 'We couldn’t load this map'}
            </h1>
            <p>
              {mapLoadError === 'unavailable'
                ? 'The link may be invalid, or your account may not have access.'
                : mapLoadError === 'sync-timeout'
                  ? 'The map service did not connect. Check your connection and try again; your saved map has not been changed.'
                : 'Check your connection and try again. Your existing map data has not been changed.'}
            </p>
            <div className="mindmap-access-error__actions">
              <button type="button" onClick={handleOpenMyMap}>
                Open my map
              </button>
              <button
                type="button"
                className="mindmap-access-error__retry"
                onClick={handleRetryMapLoad}
              >
                Try again
              </button>
            </div>
          </div>
        </main>
      ) : (
        <NodesContainer
          key={mapSessionKey}
          onBaseNodeChange={setBaseNodeTitle}
          currentUserId={user ? user.id : null}
          currentUserName={user ? user.displayName || null : null}
          remoteMapData={isLocalReviewMode ? REVIEW_MAP_FIXTURE : remoteMapData}
          onRegisterSaveHandler={(fn) => {
            saveMindMapRef.current = fn;
          }}
          onRegisterMapFileActions={handleRegisterMapFileActions}
          onMapInitialized={() => {
            setIsMapHydrated(true);
          }}
        />
      )}
      {isLocalReviewMode && (
        <div className="local-review-indicator" data-testid="local-review-indicator">
          Local review fixture · changes reset on reload
        </div>
      )}
      {(user || remoteMapData || isMapLoading) && !mapLoadError && (isMapLoading || !isMapHydrated) && (
        <div className="mindmap-loading-overlay">
          <div className="mindmap-loading-overlay__content">
            <div className="mindmap-loading-overlay__spinner" />
            <div className="mindmap-loading-overlay__text">Loading mind map...</div>
          </div>
        </div>
      )}
      {authMessage && (
        <div style={authToastStyle}>{authMessage}</div>
      )}
      {isSigningOut && (
        <div className="mindmap-signout-overlay">
          <div className="mindmap-signout-overlay__content">
            <div className="mindmap-signout-overlay__spinner" />
            <div className="mindmap-signout-overlay__text">Signing out...</div>
          </div>
        </div>
      )}
      {process.env.NODE_ENV !== 'production' && !isLocalReviewMode && debugMapInfo && (
        <div style={debugBoxStyle}>
          <div><strong>Map scope:</strong> {debugMapInfo.scope}</div>
          {debugMapInfo.ownerUserId && (
            <div><strong>Owner:</strong> {debugMapInfo.ownerUserId}</div>
          )}
          {debugMapInfo.mindmapId && (
            <div><strong>Mindmap ID:</strong> {debugMapInfo.mindmapId}</div>
          )}
          <div><strong>Guest map:</strong> {debugMapInfo.hasGuest ? 'yes' : 'no'}</div>
        </div>
      )}
    </>
  );
};

export default App;
